import React, { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import { PrimaryButton, QuaternaryButton, SecondaryButton, TertiaryButton } from '../../buttons/buttons';
import { hasChanges as detectFormChanges, computeDiff } from '../../../utils/changeDetection';
export interface TabConfig {
  label: string;
  content: ReactNode;
}

export interface ActionButton {
  label: string;
  onClick: (changedFields?: any) => void; // Optional changedFields parameter for partial updates
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  sx?: any;
  usePartialUpdate?: boolean; // If true, only changed fields will be passed to onClick
}

export interface StandardDialogProps {
  open: boolean;
  onClose: () => void;
  title: string | ReactNode;
  /** When true, the top title/subtitle/headerActions block is omitted (saves vertical space). */
  hideHeader?: boolean;
  subtitle?: string;
  children?: ReactNode;
  tabs?: TabConfig[];
  headerActions?: ReactNode;
  primaryAction?: ActionButton;
  secondaryAction?: ActionButton;
  tertiaryAction?: ActionButton;
  quaternaryAction?: ActionButton;
  error?: string | null;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullHeight?: boolean;
  height?: string | { xs?: string; sm?: string; md?: string; lg?: string; xl?: string };
  width?: string | { xs?: string; sm?: string; md?: string; lg?: string; xl?: string };
  autoSize?: boolean;
  paperSx?: any;
  contentSx?: any;
  hasChanges?: boolean;
  currentValues?: any;
  initialValues?: any;
  normalizeValues?: (values: any) => any;
}

const StandardDialog: React.FC<StandardDialogProps> = ({
  open,
  onClose,
  title,
  hideHeader = false,
  subtitle,
  children,
  tabs,
  headerActions,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  quaternaryAction,
  error,
  maxWidth = 'xl',
  fullHeight = true,
  height,
  width,
  autoSize = false,
  paperSx,
  contentSx,
  hasChanges,
  currentValues,
  initialValues,
  normalizeValues,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = React.useState(0);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Handle primary action with change detection
  const handlePrimaryAction = () => {
    // Check for changes if currentValues and initialValues are provided
    if (currentValues !== undefined && initialValues !== undefined) {
      // detectFormChanges returns true if there ARE changes, so we negate it
      if (!detectFormChanges(currentValues, initialValues, normalizeValues)) {
        toast.info(t('No changes detected'));
        onClose();
        return;
      }

      // If partial update is enabled, compute diff and pass only changed fields
      if (primaryAction?.usePartialUpdate) {
        const changedFields = computeDiff(currentValues, initialValues, normalizeValues);
        if (Object.keys(changedFields).length === 0) {
          toast.info(t('No changes detected'));
          onClose();
          return;
        }
        if (primaryAction?.onClick) {
          primaryAction.onClick(changedFields);
        }
        return;
      }
    }

    // If changes detected or no change detection enabled, proceed with original onClick
    // Pass undefined to maintain backward compatibility (dialogs not using partial update)
    if (primaryAction?.onClick) {
      primaryAction.onClick();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleScroll = () => {
    setIsScrolling(true);
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    // Hide scrollbar after 1 second of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  const renderContent = () => {
    if (tabs && tabs.length > 0) {
      return tabs[activeTab]?.content || null;
    }
    return children;
  };

  const hasActions = primaryAction || secondaryAction || tertiaryAction;

  // Determine height: autoSize > custom height > default responsive
  const getHeight = () => {
    if (autoSize) return 'auto';
    if (height) return height;
    if (fullHeight) {
      return { xs: '100vh', sm: '95vh', md: '85vh', lg: '80vh', xl: '75vh' };
    }
    return 'auto';
  };

  // Determine width: custom width or default responsive
  const getWidth = () => {
    if (width) return width;
    return { xs: '100%', sm: '90%', md: '85%', lg: '80%' };
  };

  const modalHeight = getHeight();
  const modalWidth = getWidth();
  const hasCustomHeight = height !== undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4, md: 5 },
          m: { xs: 0, sm: 2 },
          height: modalHeight,
          maxHeight: autoSize
            ? '90vh'
            : hasCustomHeight
              ? modalHeight
              : (typeof modalHeight === 'object' ? { xs: '90vh', sm: '85vh', md: '80vh', lg: '80vh', xl: '80vh' } : '80vh'),
          minHeight: autoSize ? 'auto' : (typeof modalHeight === 'object' ? '40vh' : modalHeight), // Reduced from 50vh to 40vh for less empty space
          width: modalWidth,
          maxWidth: typeof modalWidth === 'object' ? 'none' : (modalWidth || { xs: '100%', sm: '90%', md: '85%', lg: '80%' }),
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
          backgroundImage: isDarkMode
            ? 'linear-gradient(180deg, #1f1f1f 0%, #1a1a1a 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
          boxShadow: 'none',
          overflow: 'hidden',
          ...paperSx,
        },
      }}
      fullScreen={false}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(30, 30, 30, 0.75)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogContent sx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: autoSize ? 'auto' : '100%',
        overflow: 'hidden'
      }}>
        {/* Header */}
        {!hideHeader && (
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderBottom: '1px solid border.divider',
              background: 'linear-gradient(135deg, background.grey 0%, background.white 100%)',
            }}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: tabs && tabs.length > 0 ? { xs: 1, sm: 1.5 } : { xs: 0.5, sm: 0.75 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {typeof title === 'string' ? (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: 'text.dark',
                      mb: 0.5,
                      fontSize: (theme: any) => theme.typography.fontSizes.heading2
                    }}
                  >
                    {title}
                  </Typography>
                ) : (
                  <Box sx={{ mb: 0.25 }}>
                    {title}
                  </Box>
                )}
                {subtitle && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.grey',
                      fontWeight: 500,
                      fontSize: (theme: any) => theme.typography.fontSizes.body
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {headerActions && <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-end' } }}>{headerActions}</Box>}
            </Box>
          </Box>
        )}

        {/* Tabs */}
        {tabs && tabs.length > 0 && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={styles.tabStyle}
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        )}

        {/* Content */}
        <Box sx={{
          flex: autoSize ? '0 1 auto' : 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0
        }}>
          {error && (
            <Alert severity="error" sx={{ m: { xs: 2, sm: 2.5, md: 3 }, mb: 0 }}>
              {error}
            </Alert>
          )}
          <Box
            onScroll={handleScroll}
            sx={{
              flex: autoSize ? '0 1 auto' : 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: autoSize ? 'none' : (hasCustomHeight ? 'none' : 'calc(100vh - 300px)'),
              // Add padding-right to prevent text hugging scrollbar
              pr: { xs: 1, sm: 1.5 },
              // Subtle scrollbar styling - hidden by default, visible on scroll/hover
              '&::-webkit-scrollbar': {
                width: '7px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDarkMode
                  ? (isScrolling ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0)')
                  : (isScrolling ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0)'),
                borderRadius: '4px',
                border: '1px solid transparent',
                backgroundClip: 'padding-box',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(0, 0, 0, 0.25)',
                },
              },
              // Show scrollbar on hover even when not scrolling
              '&:hover': {
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.15)'
                    : 'rgba(0, 0, 0, 0.15)',
                },
              },
              // Firefox scrollbar styling - hidden by default
              scrollbarWidth: 'thin',
              scrollbarColor: isDarkMode
                ? (isScrolling ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(255, 255, 255, 0) transparent')
                : (isScrolling ? 'rgba(0, 0, 0, 0.2) transparent' : 'rgba(0, 0, 0, 0) transparent'),
              ...contentSx,
            }}
          >
            {renderContent()}
          </Box>
        </Box>

        {/* Actions */}
        {hasActions && (
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderTop: '1px solid border.divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: { xs: 1.5, sm: 2 },
              background: 'background.grey',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              '& > *': {
                width: { xs: '100%', sm: 'auto' }
              }
            }}
          >
            {secondaryAction && (
              <SecondaryButton
                onClick={secondaryAction.onClick}
                variant={secondaryAction.variant}
                color={secondaryAction.color}
                disabled={secondaryAction.disabled}
                startIcon={secondaryAction.startIcon}
                sx={{
                  ...(isDarkMode && {
                    color: 'rgba(255, 255, 255, 0.95)', // Using white with 0.95 opacity (theme white is #ffffff)
                    borderColor: 'border.whiteLight',
                    '&:hover': {
                      color: 'text.white',
                      borderColor: 'border.whiteMedium',
                      backgroundColor: 'background.whiteLight',
                    },
                    '&:disabled': {
                      color: (theme: any) => theme.palette.background.whiteExtraDark, // ~40% opacity, close to 30%
                      borderColor: 'border.whiteLight',
                    },
                  }),
                  ...secondaryAction.sx,
                }}
              >
                {secondaryAction.label}
              </SecondaryButton>
            )}
            {tertiaryAction && (
              <TertiaryButton
                onClick={tertiaryAction.onClick}
                variant={tertiaryAction.variant}
                color={tertiaryAction.color}
                disabled={tertiaryAction.disabled}
                startIcon={tertiaryAction.startIcon}
                sx={{ ...tertiaryAction.sx }}
              >
                {tertiaryAction.label}
              </TertiaryButton>
            )}
            {quaternaryAction && (
              <QuaternaryButton
                onClick={quaternaryAction.onClick}
                variant={quaternaryAction.variant}
                color={quaternaryAction.color}
                disabled={quaternaryAction.disabled}
                startIcon={quaternaryAction.startIcon}
                sx={{ ...quaternaryAction.sx }}
              >
                {quaternaryAction.label}
              </QuaternaryButton>
            )}
            {primaryAction && (
              <PrimaryButton
                onClick={handlePrimaryAction}
                variant={primaryAction.variant || 'contained'}
                color={primaryAction.color}
                disabled={primaryAction.disabled || hasChanges === false}
                loading={primaryAction.loading}
                startIcon={primaryAction.startIcon}
                sx={{
                  ...(isDarkMode && {
                    backgroundColor: '#242424',
                    border: '1px solid',
                    borderColor: 'text.purple',
                    color: 'text.white',
                    '&:hover': {
                      backgroundColor: (theme: any) => theme.palette.background.purpleHover || theme.palette.text.purple,
                      borderColor: 'text.purple',
                    },
                    '&:disabled': {
                      backgroundColor: 'background.whiteLight',
                      borderColor: 'background.whiteLight',
                      color: 'background.whiteMedium',
                    },
                  }),
                  ...(!isDarkMode && {
                    '&:hover': {
                      backgroundColor: 'background.black',
                    },
                    '&:disabled': {
                      backgroundColor: (theme: any) => alpha(theme.palette.text.dark, 0.06),
                      color: (theme: any) => alpha(theme.palette.text.dark, 0.38),
                      borderColor: (theme: any) => alpha(theme.palette.text.dark, 0.12),
                    },
                  }),
                  ...primaryAction.sx,
                }}
              >
                {primaryAction.label}
              </PrimaryButton>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StandardDialog;

const styles = {
  tabStyle: {
    borderBottom: 1,
    borderColor: 'divider',
    '& .MuiTab-root': {
      color: 'text.grey',
      '&.Mui-selected': {
        color: 'text.purple',
        fontWeight: 600,
      },
      '&:hover': {
        color: 'text.purple',
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: 'text.purple',
    },
  },
};
